import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Tags, ShoppingCart, Users, TicketPercent, Truck, Image, Settings, LogOut, Menu, ExternalLink, ShieldCheck, Wallet, Calculator } from "lucide-react";
import { api } from "../../lib/api";

export const LINKS = [
  ["/admin", "لوحة التحكم", LayoutDashboard, "reports"],
  ["/admin/orders", "الطلبات", ShoppingCart, "orders"],
  ["/admin/products", "المنتجات والمخزون", Package, "products"],
  ["/admin/categories", "التصنيفات", Tags, "categories"],
  ["/admin/coupons", "الكوبونات", TicketPercent, "coupons"],
  ["/admin/customers", "العملاء", Users, "customers"],
  ["/admin/shipping", "الشحن ومناطق التوصيل", Truck, "shipping"],
  ["/admin/content", "محتوى الرئيسية", Image, "content"],
  ["/admin/settings", "الإعدادات", Settings, "settings"],
  ["/admin/finance", "الأرباح والمحفظة", Wallet, "finance"],
  ["/admin/calculator", "حاسبة التسعير", Calculator, "products"],
  ["/admin/staff", "الموظفون والصلاحيات", ShieldCheck, "owner"],
];

const can = (admin, perm) => admin.role === "owner" || (perm !== "owner" && admin.permissions?.includes(perm));
const permFor = (path) => (path === "/admin" ? "reports" : LINKS.slice(1).find(([to]) => path.startsWith(to))?.[3]);

export default function AdminLayout() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [admin, setAdmin] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/auth/me").then((r) => setAdmin(r.data)).catch(() => nav("/admin/login", { replace: true }));
  }, [nav]);

  const logout = async () => {
    await api.post("/admin/auth/logout");
    nav("/admin/login", { replace: true });
  };

  if (!admin) return <div className="min-h-screen bg-gray-50" />;

  const links = LINKS.filter(([, , , p]) => can(admin, p));
  const allowed = can(admin, permFor(pathname));
  if (!allowed && pathname === "/admin" && links[0]) return <Navigate to={links[0][0]} replace />;

  return (
    <div dir="rtl" data-testid="admin-layout" className="min-h-screen bg-[#F8F9FA] text-gray-900" style={{ fontFamily: "Tajawal, sans-serif" }}>
      <aside className={`fixed inset-y-0 right-0 z-40 w-64 border-l border-gray-200 bg-white transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex h-16 items-center gap-3 border-b border-gray-200 px-5" dir="ltr">
          <span className="font-latin text-lg tracking-[0.35em]">QAMAR</span><span className="h-5 w-px bg-gold" /><span className="text-lg" style={{ fontFamily: "Amiri" }}>قمر</span>
        </div>
        <nav className="space-y-0.5 p-3">
          {links.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${to.split("/")[2] || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-ink text-white" : "text-gray-600 hover:bg-gray-100"}`}>
              <Icon size={17} strokeWidth={1.6} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute inset-x-0 bottom-0 space-y-1 border-t border-gray-200 p-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"><ExternalLink size={16} />عرض المتجر</a>
          <button data-testid="admin-logout-button" onClick={logout} className="flex w-full items-center gap-3 rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"><LogOut size={16} />تسجيل الخروج</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:mr-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-gray-200 bg-white/90 px-5 backdrop-blur">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-button"><Menu size={20} /></button>
          <span className="text-sm text-gray-500">{admin.name} · {admin.email} · {admin.role === "owner" ? "مالك" : "موظف"}</span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{allowed ? <Outlet context={{ admin }} /> : <p data-testid="admin-no-access" className="rounded-md border border-gray-200 bg-white p-10 text-center text-gray-500">لا تملك صلاحية الوصول لهذه الصفحة</p>}</main>
      </div>
    </div>
  );
}
